import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import type { FeedbackOrigin } from "@productlane/db"
import type { IconType } from "@productlane/ui"
import { FeedbackOriginTextRecord } from "@productlane/lib"
import { cn } from "@productlane/lib/src/browser"
import { CommandItem } from "@productlane/ui/src/components/internal/command"

import type { SharedFilterItemsProps } from "./types"
import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { useThreadsFilterState } from "../threads-filter-state"

interface OriginEntry {
	key: string
	label: string
	Icon: IconType
	origins: FeedbackOrigin[]
}

export function OriginFilterItems({ onSelect }: SharedFilterItemsProps) {
	const {
		origins: selectedOrigins,
		addOrigin,
		removeOrigin,
	} = useThreadsFilterState()

	const GROUPED_ORIGINS: OriginEntry[] = [
		{
			key: "slack",
			label: "Slack Connect",
			Icon: FeedbackOriginIconRecord.SLACK_CONNECT,
			origins: ["SLACK", "SLACK_CONNECT"],
		},
		{
			key: "intercom",
			label: "Intercom",
			Icon: FeedbackOriginIconRecord.INTERCOM,
			origins: ["INTERCOM", "INTERCOM_ATTACHMENT"],
		},
		{
			key: "zendesk",
			label: "Zendesk",
			Icon: FeedbackOriginIconRecord.ZENDESK,
			origins: ["ZENDESK", "ZENDESK_ATTACHMENT"],
		},
	]

	const INDIVIDUAL_ORIGINS: FeedbackOrigin[] = [
		"EMAIL",
		"PORTAL",
		"SUPPORT_PORTAL",
		"WIDGET",
		"AI_CHAT",
		"LIVE_CHAT",
		"INAPP",
		"LINEAR",
		"API",
		"CUSTOM",
		"TEAMS",
		"ZAPIER",
		"HUBSPOT",
		"CALENDAR",
	]

	const toggleGrouped = (entry: OriginEntry) => {
		const isSelected = entry.origins.some((o) => selectedOrigins.includes(o))
		if (isSelected) {
			entry.origins.forEach((o) => removeOrigin(o))
		} else {
			entry.origins.forEach((o) => addOrigin(o))
		}
		onSelect()
	}

	const isGroupedSelected = (entry: OriginEntry) =>
		entry.origins.some((o) => selectedOrigins.includes(o))

	return (
		<>
			{GROUPED_ORIGINS.map((entry) => (
				<CommandItem
					key={entry.key}
					value={entry.label}
					Icon={entry.Icon}
					className="h-10"
					onSelect={() => toggleGrouped(entry)}
				>
					<span className="flex w-full items-center justify-between">
						<span className="truncate">{entry.label}</span>
						<Check
							className={cn("h-4 w-4 shrink-0", {
								"opacity-100": isGroupedSelected(entry),
								"opacity-0": !isGroupedSelected(entry),
							})}
						/>
					</span>
				</CommandItem>
			))}
			{INDIVIDUAL_ORIGINS.map((origin) => {
				const Icon = FeedbackOriginIconRecord[origin]
				const label = FeedbackOriginTextRecord[origin].replace("via ", "")
				return (
					<CommandItem
						key={origin}
						Icon={Icon}
						className="h-10"
						onSelect={() => {
							if (selectedOrigins.includes(origin)) {
								removeOrigin(origin)
							} else {
								addOrigin(origin)
							}
							onSelect()
						}}
					>
						<span className="flex w-full items-center justify-between">
							<span className="truncate">{label}</span>
							<Check
								className={cn("h-4 w-4 shrink-0", {
									"opacity-100": selectedOrigins.includes(origin),
									"opacity-0": !selectedOrigins.includes(origin),
								})}
							/>
						</span>
					</CommandItem>
				)
			})}
		</>
	)
}
